/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * AuthLayout — Unified Login / Signup sliding auth experience.
 * The dark branding panel stays fixed while the white content area
 * smoothly slides between Login form and multi-step Signup wizard.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  Organization, OrgPlan, OrgStatus, User, UserRole, Department, UserStatus,
} from '../domain/entities';
import { getRepository, isRemoteMode } from '../config/repository';
import { setAuthToken } from '../data/ApiModuleRepository';
import { PLAN_LABELS } from '../core/planGating';
import {
  Zap, LogIn, ArrowRight, ArrowLeft, Eye, EyeOff, AlertCircle,
  Building2, UserCircle, CreditCard, CheckCircle, Lock, Mail, Briefcase,
} from 'lucide-react';

// ── Shared Constants ────────────────────────────────────────────────────────

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
const repo = () => getRepository();

const PLAN_PRICES: Record<OrgPlan, string> = {
  [OrgPlan.FREE]:       '$0 / month',
  [OrgPlan.BASIC]:      '$19 / month',
  [OrgPlan.PRO]:        '$49 / month',
  [OrgPlan.ENTERPRISE]: 'Custom pricing',
};

const PLAN_HIGHLIGHTS: Record<OrgPlan, string[]> = {
  [OrgPlan.FREE]:       ['1 Hub', '2 Users', '50 Riders', 'Core features'],
  [OrgPlan.BASIC]:      ['3 Hubs', '5 Users', '200 Riders', 'Analytics & Logs'],
  [OrgPlan.PRO]:        ['10 Hubs', '20 Users', '1,000 Riders', 'API & Webhooks', 'Analytics'],
  [OrgPlan.ENTERPRISE]: ['Unlimited everything', 'White-label branding', 'Priority support', 'SLA guarantee'],
};

const SELF_SERVE_PLANS: OrgPlan[] = [OrgPlan.FREE, OrgPlan.PRO, OrgPlan.ENTERPRISE];
const SIGNUP_STEPS = ['Company', 'Account', 'Security', 'Plan'];
const derivedSlug = (n: string) => n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// ── Props ───────────────────────────────────────────────────────────────────

export interface AuthLayoutProps {
  /** Initial mode — which form to show first */
  initialMode?: 'login' | 'signup';
  /** Demo users for local dev */
  demoUsers: User[];
  /** Called after successful login/signup */
  switchUser: (u: User) => Promise<void>;
  /** Called when signup creates a user who can immediately enter */
  onComplete: (u: User) => Promise<void>;
  /** CMS branding from platform settings */
  cms?: {
    platformName?: string;
    logoUrl?: string;
    loginWelcomeText?: string;
    loginSidePanelImageUrl?: string;
  };
}

// ── Slide animation helpers ─────────────────────────────────────────────────

type SlideDirection = 1 | -1;

const slideVariants = {
  enter: (d: SlideDirection) => ({
    x: d > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (d: SlideDirection) => ({
    x: d > 0 ? -300 : 300,
    opacity: 0,
  }),
};

// ── Step Indicator (Signup) ─────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {SIGNUP_STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                done ? 'bg-emerald-500 text-white' : active ? 'bg-indigo-600 text-white' : 'bg-zinc-100 text-zinc-400'
              }`}>
                {done ? <CheckCircle size={14} /> : i + 1}
              </div>
              <span className={`text-xs font-bold hidden sm:block ${active ? 'text-zinc-900' : 'text-zinc-400'}`}>
                {label}
              </span>
            </div>
            {i < SIGNUP_STEPS.length - 1 && (
              <div className={`h-px w-6 ${i < current ? 'bg-emerald-400' : 'bg-zinc-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── parseArray helper ───────────────────────────────────────────────────────

function parseArray(value: unknown): string[] | undefined {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value.map(item => String(item));
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(item => String(item));
    } catch {
      return value.trim() ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
    }
  }
  return [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main AuthLayout Component
// ═══════════════════════════════════════════════════════════════════════════════

export function AuthLayout({ initialMode = 'login', demoUsers, switchUser, onComplete, cms }: AuthLayoutProps) {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');

  // ── Mode: 'login' | 'signup' ──
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [slideDir, setSlideDir] = useState<SlideDirection>(1);

  // ── Login state ──
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // ── Signup state ──
  const [signupStep, setSignupStep] = useState(0);
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [industry, setIndustry] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const initialPlan = planParam && Object.values(OrgPlan).includes(planParam as OrgPlan) ? (planParam as OrgPlan) : OrgPlan.PRO;
  const [selectedPlan, setSelectedPlan] = useState<OrgPlan>(initialPlan);
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [completion, setCompletion] = useState<{ message: string; canSignIn: boolean } | null>(null);

  // ── Platform settings for signup ──
  const [platformName, setPlatformName] = useState(cms?.platformName ?? 'ClickIt Platform');
  const [allowSignups, setAllowSignups] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load platform settings (for signup availability check)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isRemoteMode()) {
          const res = await fetch(`${API_BASE}/api/platform-settings/public`);
          if (res.ok) {
            const payload = await res.json() as { data?: { platformName?: string; allowSignups?: boolean } };
            if (!cancelled) {
              setPlatformName(payload.data?.platformName ?? 'ClickIt Platform');
              setAllowSignups(payload.data?.allowSignups !== false);
            }
          }
        } else {
          const s = await repo().getPlatformSettings();
          if (!cancelled) {
            setPlatformName(s.platformName);
            setAllowSignups(s.allowSignups);
          }
        }
      } catch { /* keep defaults */ }
      finally { if (!cancelled) setSettingsLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sync platformName from cms prop
  useEffect(() => {
    if (cms?.platformName) setPlatformName(cms.platformName);
  }, [cms?.platformName]);

  const brandName = cms?.platformName ?? platformName;

  // ── Mode transitions ──
  const goToSignup = () => {
    setSlideDir(1);
    setMode('signup');
    setSignupStep(0);
    setSignupError('');
    setCompletion(null);
  };

  const goToLogin = () => {
    setSlideDir(-1);
    setMode('login');
    setLoginError('');
  };

  // ═══════════════════════════════════════════════════════════════
  // Login handler
  // ═══════════════════════════════════════════════════════════════

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!email.trim()) { setLoginError('Please enter your email address.'); return; }
    if (!password) { setLoginError('Please enter your password.'); return; }

    setLoginLoading(true);
    try {
      if (isRemoteMode()) {
        const res = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), password }),
        });
        const data = await res.json() as any;
        if (!res.ok) { setLoginError(data.message ?? 'Invalid email or password.'); return; }
        setAuthToken(data.token);
        const apiUser: User = {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
          status: data.user.status,
          accountType: data.user.accountType ?? data.user.account_type,
          orgId: data.user.orgId ?? data.user.org_id,
          mustChangePassword: data.user.mustChangePassword ?? !!data.user.must_change_password,
          canInspectTenants: data.user.canInspectTenants ?? !!data.user.can_inspect_tenants,
          assignedModules: parseArray(data.user.assignedModules ?? data.user.assigned_modules) as User['assignedModules'],
          assignedBrands: parseArray(data.user.assignedBrands ?? data.user.assigned_brands),
          assignedBranches: parseArray(data.user.assignedBranches ?? data.user.assigned_branches ?? data.user.assignedHubs ?? data.user.assigned_hubs),
          tabAccess: parseArray(data.user.tabAccess ?? data.user.tab_access),
          assignedDepartments: parseArray(data.user.assignedDepartments ?? data.user.assigned_departments),
        } as User;
        await switchUser(apiUser);
      } else {
        await new Promise(r => setTimeout(r, 400));
        const matched = demoUsers.find(u => u.email.toLowerCase() === email.trim().toLowerCase());
        if (!matched) { setLoginError('Invalid email or password.'); return; }
        if (!matched.passwordHash || matched.passwordHash !== password) { setLoginError('Invalid email or password.'); return; }
        await switchUser(matched);
      }
    } catch { setLoginError('Something went wrong. Please try again.'); }
    finally { setLoginLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════════
  // Signup step handlers
  // ═══════════════════════════════════════════════════════════════

  const handleStep1 = () => {
    if (!orgName.trim()) { setSignupError('Organisation name is required.'); return; }
    setSignupError(''); setSignupStep(1);
  };

  const handleStep2 = () => {
    if (!adminName.trim()) { setSignupError('Your full name is required.'); return; }
    if (!adminEmail.trim()) { setSignupError('Email address is required.'); return; }
    if (!adminEmail.includes('@')) { setSignupError('Enter a valid email address.'); return; }
    setSignupError(''); setSignupStep(2);
  };

  const handleStep3 = () => {
    if (!signupPassword) { setSignupError('Password is required.'); return; }
    if (signupPassword.length < 8 || !/\d/.test(signupPassword)) {
      setSignupError('Password must be at least 8 characters and include at least 1 number.'); return;
    }
    if (signupPassword !== confirmPassword) { setSignupError('Passwords do not match.'); return; }
    setSignupError(''); setSignupStep(3);
  };

  const getCompletionMessage = (status: OrgStatus) => {
    if (status === OrgStatus.ACTIVE) return 'Your workspace is ready. You can sign in now.';
    if (status === OrgStatus.PENDING_PAYMENT) return 'Signup received. Waiting for payment confirmation.';
    return 'Signup received. Waiting for platform approval.';
  };

  const handleSubmit = async () => {
    if (!allowSignups) { setSignupError('New signups are currently disabled.'); return; }
    setSignupLoading(true);
    setSignupError('');
    try {
      if (isRemoteMode()) {
        const res = await fetch(`${API_BASE}/api/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orgName: orgName.trim(),
            slug: slug.trim() || derivedSlug(orgName),
            industry: industry.trim(),
            adminName: adminName.trim(),
            adminEmail: adminEmail.trim(),
            password: signupPassword,
            plan: selectedPlan,
          }),
        });
        const payload = await res.json() as { message?: string; data?: { canSignIn?: boolean } };
        if (!res.ok) { setSignupError(payload.message ?? 'Something went wrong.'); return; }
        setCompletion({ message: payload.message ?? 'Signup complete.', canSignIn: !!payload.data?.canSignIn });
        return;
      }
      // Local mode
      const settings = await repo().getPlatformSettings();
      const isPaid = selectedPlan !== OrgPlan.FREE;
      const orgStatus = isPaid
        ? (settings.autoApproveNewOrganizations ? OrgStatus.PENDING_PAYMENT : OrgStatus.PENDING_APPROVAL)
        : (settings.autoApproveNewOrganizations ? OrgStatus.ACTIVE : OrgStatus.PENDING_APPROVAL);

      const newOrg: Organization = {
        id: crypto.randomUUID(), name: orgName.trim(),
        slug: slug.trim() || derivedSlug(orgName), industry: industry.trim() || undefined,
        status: orgStatus, plan: selectedPlan, createdAt: Date.now(), enabledApps: [], manualUserApproval: false,
      };
      await repo().saveOrganization(newOrg);
      const defaultDept: Department = { id: `dept-${newOrg.id}-general`, name: 'General', orgId: newOrg.id, order: 0, linkedModules: [] };
      await repo().saveDepartment(defaultDept);
      const newAdmin: User = {
        id: crypto.randomUUID(), name: adminName.trim(), email: adminEmail.trim(),
        role: UserRole.ADMIN, status: UserStatus.ACTIVE,
        assignedBrands: [], assignedBranches: [], assignedModules: [],
        orgId: newOrg.id, passwordHash: signupPassword, mustChangePassword: false,
      };
      await repo().saveUser(newAdmin);
      if (orgStatus === OrgStatus.ACTIVE) { await onComplete(newAdmin); return; }
      setCompletion({ message: getCompletionMessage(orgStatus), canSignIn: false });
    } catch (err: any) {
      setSignupError(err?.message ?? 'Something went wrong.');
    } finally { setSignupLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen flex bg-white">
      {/* ── Left dark branding panel (persistent) ────────────────── */}
      <div className="hidden lg:flex w-[420px] bg-slate-900 flex-col justify-between p-10 relative overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 to-slate-900 pointer-events-none" />
        {cms?.loginSidePanelImageUrl && (
          <div className="absolute inset-0 pointer-events-none">
            <img src={cms.loginSidePanelImageUrl} alt="" className="w-full h-full object-cover opacity-20" />
          </div>
        )}

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          {cms?.logoUrl ? (
            <img src={cms.logoUrl} alt={brandName} className="w-10 h-10 rounded-xl object-contain" />
          ) : (
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Zap size={20} className="text-white" />
            </div>
          )}
          <div>
            <div className="text-white font-black text-base tracking-tight">{brandName}</div>
            <div className="text-indigo-400 text-[10px] font-bold uppercase tracking-widest">Platform</div>
          </div>
        </div>

        {/* Tagline — reacts to mode */}
        <div className="relative space-y-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {mode === 'login' ? (
                <>
                  <blockquote className="text-2xl font-black text-white leading-tight">
                    {cms?.loginWelcomeText || <>"One platform.<br />Every operational flow."</>}
                  </blockquote>
                  <p className="text-slate-400 text-sm leading-relaxed mt-4">
                    Modular apps for managing your data, teams, and workflows — all under one roof.
                  </p>
                </>
              ) : (
                <>
                  <blockquote className="text-2xl font-black text-white leading-tight">
                    Get your workspace<br />up and running.
                  </blockquote>
                  <p className="text-slate-400 text-sm leading-relaxed mt-4">
                    Set up your organisation in minutes. Choose a plan, invite your team, and go live.
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Feature highlights */}
        <div className="relative space-y-3">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Enterprise Ready</p>
          {[
            { label: 'Multi-Tenant Architecture', desc: 'Isolated data per organization' },
            { label: 'Role-Based Access Control', desc: '8 configurable permission levels' },
            { label: 'Modular App Marketplace', desc: 'Install only what you need' },
          ].map(item => (
            <div key={item.label} className="flex items-start gap-2.5 opacity-70">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
              <div>
                <div className="text-xs font-medium text-slate-300">{item.label}</div>
                <div className="text-[10px] text-slate-500">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right content panel (slides) ─────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 overflow-hidden relative">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-6">
            {cms?.logoUrl ? (
              <img src={cms.logoUrl} alt={brandName} className="w-8 h-8 rounded-lg object-contain" />
            ) : (
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Zap size={16} className="text-white" />
              </div>
            )}
            <span className="text-base font-black text-zinc-900">{brandName}</span>
          </div>

          <AnimatePresence mode="wait" custom={slideDir}>
            {/* ═════════ LOGIN FORM ═════════ */}
            {mode === 'login' && (
              <motion.div
                key="login"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'tween', duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="space-y-7"
              >
                <div>
                  <h1 className="text-2xl font-black text-zinc-900">Welcome back</h1>
                  <p className="text-sm text-zinc-500 mt-1">Sign in to your organisation's portal.</p>
                </div>

                <form onSubmit={handleSignIn} className="space-y-5">
                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">Email Address</label>
                    <input
                      type="email" value={email}
                      onChange={e => { setEmail(e.target.value); setLoginError(''); }}
                      placeholder="name@company.com" autoComplete="email"
                      className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'} value={password}
                        onChange={e => { setPassword(e.target.value); setLoginError(''); }}
                        placeholder="Enter your password" autoComplete="current-password"
                        className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-11 transition-all"
                      />
                      <button type="button" onClick={() => setShowPassword(p => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors" tabIndex={-1}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button type="button"
                      onClick={() => alert('Password reset will be available in a future update.')}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline transition-colors">
                      Forgot password?
                    </button>
                  </div>

                  {loginError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                      <AlertCircle size={14} className="text-red-500 shrink-0" />
                      <p className="text-sm text-red-600 font-medium">{loginError}</p>
                    </div>
                  )}

                  <button type="submit" disabled={loginLoading}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white rounded-xl font-black text-sm hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    {loginLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <><LogIn size={15} /> Sign In</>
                    )}
                  </button>
                </form>

                <p className="text-center text-sm text-zinc-500">
                  New to {brandName}?{' '}
                  <button onClick={goToSignup} className="text-indigo-600 font-bold hover:underline inline-flex items-center gap-0.5">
                    Get started free <ArrowRight size={12} />
                  </button>
                </p>
              </motion.div>
            )}

            {/* ═════════ SIGNUP FORM ═════════ */}
            {mode === 'signup' && !completion && (
              <motion.div
                key="signup"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'tween', duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-black text-zinc-900">Create your account</h1>
                  <p className="text-sm text-zinc-500 mt-1">Set up your workspace and request activation.</p>
                </div>

                {!allowSignups && settingsLoaded && (
                  <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    New signups are currently disabled. Contact the platform team for access.
                  </div>
                )}

                <StepIndicator current={signupStep} />

                {/* Step 1: Company */}
                {signupStep === 0 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 size={18} className="text-indigo-600" />
                      <h2 className="font-bold text-zinc-900">Tell us about your company</h2>
                    </div>
                    {signupError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{signupError}</p>}
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Organisation Name *</label>
                      <input value={orgName} onChange={e => { setOrgName(e.target.value); setSlug(derivedSlug(e.target.value)); }}
                        className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Acme Operations" autoFocus />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Industry</label>
                      <div className="relative">
                        <Briefcase size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input value={industry} onChange={e => setIndustry(e.target.value)}
                          className="w-full rounded-xl border border-zinc-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Restaurant Operations" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">URL Slug</label>
                      <input value={slug} onChange={e => setSlug(e.target.value)}
                        className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="acme-operations" />
                      <p className="text-xs text-zinc-400 mt-1">Used in your portal URL. Auto-generated from your name.</p>
                    </div>
                    <button onClick={handleStep1} disabled={!allowSignups || !settingsLoaded}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white rounded-xl font-black text-sm hover:bg-zinc-800 transition-all">
                      Continue <ArrowRight size={15} />
                    </button>
                  </div>
                )}

                {/* Step 2: Admin Account */}
                {signupStep === 1 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-2 mb-4">
                      <UserCircle size={18} className="text-indigo-600" />
                      <h2 className="font-bold text-zinc-900">Create your admin account</h2>
                    </div>
                    {signupError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{signupError}</p>}
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Your Full Name *</label>
                      <input value={adminName} onChange={e => setAdminName(e.target.value)}
                        className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Alex Johnson" autoFocus />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Work Email *</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
                          className="w-full rounded-xl border border-zinc-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="alex@yourcompany.com" />
                      </div>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button onClick={() => { setSignupError(''); setSignupStep(0); }}
                        className="flex items-center gap-1.5 px-4 py-3 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-all">
                        <ArrowLeft size={14} /> Back
                      </button>
                      <button onClick={handleStep2}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white rounded-xl font-black text-sm hover:bg-zinc-800 transition-all">
                        Continue <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Security */}
                {signupStep === 2 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Lock size={18} className="text-indigo-600" />
                      <h2 className="font-bold text-zinc-900">Secure your admin account</h2>
                    </div>
                    {signupError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{signupError}</p>}
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Password *</label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input type="password" value={signupPassword} onChange={e => setSignupPassword(e.target.value)}
                          className="w-full rounded-xl border border-zinc-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="At least 8 characters, 1 number" autoFocus />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Confirm Password *</label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                          className="w-full rounded-xl border border-zinc-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Repeat your password" />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-400">You will use this password to access your admin workspace after approval.</p>
                    <div className="flex gap-3 pt-1">
                      <button onClick={() => { setSignupError(''); setSignupStep(1); }}
                        className="flex items-center gap-1.5 px-4 py-3 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-all">
                        <ArrowLeft size={14} /> Back
                      </button>
                      <button onClick={handleStep3}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white rounded-xl font-black text-sm hover:bg-zinc-800 transition-all">
                        Continue <ArrowRight size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Plan */}
                {signupStep === 3 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard size={18} className="text-indigo-600" />
                      <h2 className="font-bold text-zinc-900">Choose your plan</h2>
                    </div>
                    {signupError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{signupError}</p>}
                    <div className="space-y-3">
                      {SELF_SERVE_PLANS.map(plan => {
                        const active = selectedPlan === plan;
                        return (
                          <button key={plan} onClick={() => setSelectedPlan(plan)}
                            className={`w-full text-left rounded-xl border px-4 py-4 transition-all ${
                              active ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200' : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                            }`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                  active ? 'border-indigo-600' : 'border-zinc-300'
                                }`}>
                                  {active && <div className="w-2 h-2 bg-indigo-600 rounded-full" />}
                                </div>
                                <span className={`font-black text-sm ${active ? 'text-indigo-900' : 'text-zinc-900'}`}>
                                  {PLAN_LABELS[plan]}
                                </span>
                              </div>
                              <span className={`text-xs font-bold ${active ? 'text-indigo-600' : 'text-zinc-500'}`}>
                                {PLAN_PRICES[plan]}
                              </span>
                            </div>
                            <ul className="ml-6 space-y-0.5">
                              {PLAN_HIGHLIGHTS[plan].map(h => (
                                <li key={h} className="text-xs text-zinc-500 flex items-center gap-1">
                                  <CheckCircle size={10} className="text-zinc-300 shrink-0" /> {h}
                                </li>
                              ))}
                            </ul>
                          </button>
                        );
                      })}
                    </div>
                    <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-500">
                      Free workspaces can activate immediately when auto-approval is enabled. Paid plans remain gated until the platform team reviews the request.
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button onClick={() => { setSignupError(''); setSignupStep(2); }}
                        className="flex items-center gap-1.5 px-4 py-3 border border-zinc-200 rounded-xl text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-all">
                        <ArrowLeft size={14} /> Back
                      </button>
                      <button onClick={handleSubmit} disabled={signupLoading}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-xl font-black text-sm hover:bg-indigo-700 disabled:opacity-50 transition-all">
                        {signupLoading ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>Create my account <ArrowRight size={15} /></>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Back to Sign In */}
                <p className="text-xs text-zinc-400 mt-6 text-center">
                  Already have an account?{' '}
                  <button onClick={goToLogin} className="text-indigo-600 font-bold hover:underline">Sign in</button>
                </p>
              </motion.div>
            )}

            {/* ═════════ SIGNUP COMPLETION ═════════ */}
            {mode === 'signup' && completion && (
              <motion.div
                key="completion"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'tween', duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="text-center space-y-5"
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                  <CheckCircle size={28} />
                </div>
                <div>
                  <h1 className="text-2xl font-black text-zinc-900">Signup submitted</h1>
                  <p className="text-sm text-zinc-500 mt-2">{completion.message}</p>
                </div>
                <div className="space-y-3 pt-2">
                  {completion.canSignIn ? (
                    <button onClick={goToLogin}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white rounded-xl font-black text-sm hover:bg-zinc-800 transition-all">
                      Go to sign in <ArrowRight size={15} />
                    </button>
                  ) : (
                    <button onClick={goToLogin}
                      className="w-full flex items-center justify-center gap-2 py-3 border border-zinc-200 text-zinc-700 rounded-xl font-black text-sm hover:bg-zinc-50 transition-all">
                      Back to sign in
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
